import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  evidenceApi,
  evidenceSubmissionsApi,
  raciApi,
  linkedArtefactsApi,
  peopleApi,
  assetsApi,
  departmentsApi,
  documentsApi,
  registerConfigApi,
  type Deliverable,
} from '../services/api';
import type { FieldConfig, FieldValues, Person, Asset, Department } from '../types/models';
import { FieldRenderer } from '../components/FieldRenderer';
import { IdentityCard } from '../components/IdentityCard';
import { CollectionScheduleCard } from '../components/CollectionScheduleCard';
import { ReviewNotesCard } from '../components/ReviewNotesCard';
import { DocumentManager } from '../components/DocumentManager';
import { RaciAssignments } from '../components/RaciAssignments';
import { LinkedArtefacts } from '../components/LinkedArtefacts';
import { Dropzone } from '../components/Dropzone';
import { TeamSubmissionPanel } from '../components/evidence/TeamSubmissionPanel';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useSyncRaciFromControls } from '../hooks/useSyncRaciFromControls';
import { getMissingRequiredFields } from '../utils/fieldValidation';
import { DEFAULT_EVIDENCE_FIELDS } from './RegisterItemDetail';

// ── Replaces RegisterItemDetail's generic tabbed shell + EvidenceScheduleTab.tsx for Evidence
// specifically (RUB-50353 evidence overhaul, Evidence Record rebuild). Full concept:
// https://claude.ai/code/artifact/0e579519-d03d-4318-91c0-93b45bcf7db5 - single continuous
// record, no tab bar, a permanent Health & Rollup zone, and the multi-team Submissions history
// rebuilt as a compact Team Ledger table (one row per team, not per round). Other register kinds
// (Risk, Control, Issue, etc.) are untouched - they still render through RegisterItemDetail. ──

interface Submission {
  submissionId: string;
  evidenceId: string;
  submittedBy: string;
  submittedByName?: string;
  submittedAt: string;
  status: 'Submitted' | 'In Review' | 'Accepted' | 'Rejected' | 'Clarification Needed';
  timeliness: 'On Time' | 'Late';
  notes?: string;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  reviewNote?: string;
  strengthOverride?: string;
  comments?: Array<{ commentId: string; authorId: string; authorName?: string; text: string; createdAt: string }>;
  teamName?: string;
  teamDepartmentId?: string;
  deliverables?: Deliverable[];
}

const UNNAMED_TEAM = 'General';

interface TimelineEntry {
  at: string;
  kind: 'submitted' | 'comment' | 'decision';
  author: string;
  tone: string;
  label: string;
  body?: string;
}

// Chronological submitted -> comment -> decision feed for ONE round, moved from the old
// EvidenceScheduleTab.tsx's buildTimeline - the round this is built from is the currently
// SELECTED round (which may be an earlier one, not necessarily the team's latest), so a
// historical round's own decision is visible again, not just the current one's.
function buildTimeline(sub: Submission): TimelineEntry[] {
  const entries: TimelineEntry[] = [
    {
      at: sub.submittedAt,
      kind: 'submitted',
      author: sub.submittedByName || sub.submittedBy,
      tone: 'neutral',
      label: 'Submitted',
      body: sub.notes,
    },
  ];
  (sub.comments || []).forEach((c) => {
    entries.push({ at: c.createdAt, kind: 'comment', author: c.authorName || c.authorId, tone: 'info', label: 'Comment', body: c.text });
  });
  if (sub.reviewedAt && (sub.status === 'Accepted' || sub.status === 'Rejected' || sub.status === 'Clarification Needed')) {
    entries.push({
      at: sub.reviewedAt,
      kind: 'decision',
      author: sub.reviewedByName || sub.reviewedBy || '',
      tone: STATUS_TONE[sub.status] || 'neutral',
      label: sub.status,
      body: sub.reviewNote,
    });
  }
  return entries.sort((a, b) => a.at.localeCompare(b.at));
}
const STRENGTH_OPTIONS = ['No Assurance', 'Limited Assurance', 'Substantial Assurance', 'Full Assurance'];
const DELIVERABLE_STATUSES = ['Pending', 'In Progress', 'Done'];

const STATUS_TONE: Record<string, string> = {
  Submitted: 'warning',
  'In Review': 'warning',
  Accepted: 'success',
  Rejected: 'danger',
  'Clarification Needed': 'warning',
};

const fmtRelative = (iso?: string) => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Calendar-day difference, both sides normalised to local midnight - same approach as
// CollectionScheduleCard's daysBetween, so "the due date itself" always resolves to exactly 0
// (never -0/-1 depending on time-of-day) and this and computeHealth's own overdueByDate agree.
const daysUntil = (iso?: string): number | null => {
  if (!iso) return null;
  const target = new Date(iso);
  if (isNaN(target.getTime())) return null;
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
};

// Timeliness chip for a team that has not submitted yet. Thresholds follow computeHealth and
// CollectionScheduleCard's computeScheduleStatus (overdue = danger, <=7 days = warning) so the
// chip never contradicts the Health banner above it.
const dueChip = (iso?: string): { tone: string; label: string } | null => {
  const days = daysUntil(iso);
  if (days === null) return null;
  if (days < 0) return { tone: 'danger', label: `Overdue by ${-days} day${days === -1 ? '' : 's'}` };
  if (days === 0) return { tone: 'warning', label: 'Due today' };
  if (days <= 7) return { tone: 'warning', label: `Due in ${days} day${days === 1 ? '' : 's'}` };
  return { tone: 'neutral', label: `Due ${new Date(iso!).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` };
};

// Bootstrap has no `neutral` variant (no --bs-neutral, bg-label-neutral or text-neutral exist);
// its equivalent of the design system's neutral tone is `secondary`.
const bsTone = (tone: string) => (tone === 'neutral' ? 'secondary' : tone);

// ── Requirements Matrix: assignment + status derivation ───────────────────────────────────────
// One predicate, reused for every cell AND every rollup badge, so the grid and its "N/M" counts
// can never disagree. Absent/empty excludedTeams = applies to every team, present and future -
// the default every evidence record predating this feature already satisfies (zero migration).
// An EXCLUSION list, not inclusion: excluding one team never touches any other team's coverage,
// including one added to the roster later - see excludedTeams's own doc comment in api.ts.
const appliesToTeam = (req: Deliverable, team: string): boolean => !req.excludedTeams?.includes(team);

type CellStatus = 'done' | 'in-progress' | 'not-started' | 'not-applicable';
function cellStatus(req: Deliverable, team: string, latest: Submission | undefined): CellStatus {
  if (!appliesToTeam(req, team)) return 'not-applicable';
  if (!latest) return 'not-started';
  const d = latest.deliverables?.find((x) => x.id === req.id);
  if (!d || !d.status || d.status === 'Pending') return 'not-started';
  if (d.status === 'In Progress') return 'in-progress';
  return 'done';
}

// Small attention dot for a team's avatar: null when nothing needs the reviewer's attention (a
// team with no submission yet is already flagged by its own "Not submitted" chip - a second dot
// saying the same thing isn't new information), 'warning' while a round awaits decision, 'danger'
// once it's rejected or sent back for clarification.
const attentionTone = (latest: Submission | undefined): 'warning' | 'danger' | null => {
  if (!latest) return null;
  if (latest.status === 'Submitted' || latest.status === 'In Review') return 'warning';
  if (latest.status === 'Rejected' || latest.status === 'Clarification Needed') return 'danger';
  return null;
};

// Deterministic per-team avatar colour (identity, not status - the status chip and the
// attentionTone dot already carry status) so the same team reads as the same colour across the
// matrix header, the Team Workflow accordion, and the "As a Team" picker.
const TEAM_AVATAR_COLORS = ['#6d5cd6', '#2f9e8f', '#c9762e', '#4a7fd6', '#b23a6e', '#3f8f4f', '#8a6d3b', '#5a5fc7'];
const avatarColorFor = (team: string): string => {
  let hash = 0;
  for (let i = 0; i < team.length; i++) hash = (hash * 31 + team.charCodeAt(i)) >>> 0;
  return TEAM_AVATAR_COLORS[hash % TEAM_AVATAR_COLORS.length];
};

// Same SVG stroke-dasharray fraction-ring technique PendingAiUseCasesPanel.tsx's ChecklistRing
// already established for this app (not exported from there - a page-local const) - reused here
// on .np-checklist-ring for the Team Workflow accordion's per-team completion ring.
const FractionRing: React.FC<{ done: number; total: number; tone: string }> = ({ done, total, tone }) => {
  const size = 36;
  const stroke = 3;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = total > 0 ? Math.min(100, (done / total) * 100) : 0;
  const dash = (pct / 100) * circumference;
  return (
    <div className="np-checklist-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle className="track" cx={size / 2} cy={size / 2} r={radius} strokeWidth={stroke} />
        <circle className="fill" cx={size / 2} cy={size / 2} r={radius} strokeWidth={stroke} stroke={`var(--bs-${tone})`} strokeDasharray={`${dash} ${circumference}`} />
      </svg>
      <span className="frac">{done}/{total}</span>
    </div>
  );
};

const fmtSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileIcon = (fileType: string) => {
  const t = (fileType || '').toLowerCase();
  if (t.includes('pdf')) return 'ri-file-pdf-line text-danger';
  if (t.includes('image') || t.includes('png') || t.includes('jpg')) return 'ri-image-line text-primary';
  if (t.includes('excel') || t.includes('spreadsheet') || t.includes('xlsx')) return 'ri-file-excel-line text-success';
  if (t.includes('word') || t.includes('document') || t.includes('docx')) return 'ri-file-word-line text-info';
  return 'ri-file-line text-secondary';
};

const initials = (name: string) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const frequencyToMonths: Record<string, number> = {
  Weekly: 0.25, 'Bi-weekly': 0.5, Monthly: 1, Quarterly: 3, 'Bi-annually': 6, Annually: 12,
};
const calcDateFromFrequency = (fromDate: string, frequency: string): string | null => {
  const months = frequencyToMonths[frequency];
  if (!fromDate || months === undefined) return null;
  const d = new Date(fromDate);
  if (months < 1) d.setDate(d.getDate() + Math.round(months * 28));
  else d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0];
};

// ── Deliverables: plain repeatable rows, moved verbatim from the old EvidenceScheduleTab.tsx ──
const DeliverablesEditor: React.FC<{ value: Deliverable[]; onChange: (next: Deliverable[]) => void; compact?: boolean }> = ({
  value,
  onChange,
  compact,
}) => {
  const addRow = () => onChange([...value, { id: `DLV-${Date.now()}`, description: '', status: 'Pending', dueDate: '' }]);
  const updateRow = (id: string, patch: Partial<Deliverable>) => onChange(value.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  const removeRow = (id: string) => onChange(value.filter((d) => d.id !== id));

  return (
    <div>
      {value.length === 0 && !compact && <p className="np-sub small mb-2">No deliverables recorded yet.</p>}
      {value.map((d) => (
        <div key={d.id} className="row g-2 align-items-center mb-2">
          <div className="col-12 col-md-5">
            <input className="np-input" placeholder="Deliverable description" value={d.description} onChange={(e) => updateRow(d.id, { description: e.target.value })} />
          </div>
          <div className="col-6 col-md-3">
            <select className="np-select" value={d.status || 'Pending'} onChange={(e) => updateRow(d.id, { status: e.target.value })}>
              {DELIVERABLE_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-5 col-md-3">
            <input type="date" className="np-input" value={d.dueDate || ''} onChange={(e) => updateRow(d.id, { dueDate: e.target.value })} />
          </div>
          <div className="col-1">
            <button type="button" className="np-icon-btn np-icon-btn-danger" title="Remove" onClick={() => removeRow(d.id)}>
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>
        </div>
      ))}
      <button type="button" className="np-btn np-btn-ghost np-btn-sm mt-1" onClick={addRow}>
        <i className="ri-add-line"></i>Add deliverable
      </button>
    </div>
  );
};

// ── One requirement row's description/due-date editor, with local buffered state committed on
// blur rather than on every keystroke. A controlled input whose `value` is the SERVER value
// (req.description, sourced from evidenceDeliverables via react-query) and which also mutates on
// every keystroke fights itself: requirementsMutation's onSuccess invalidates the evidence query,
// and any re-render that lands between two keystrokes (that invalidation's own refetch, or ANY
// other row's edit, or an unrelated background refetch elsewhere on the page) snaps the input's
// displayed text back to whatever the cache held a moment ago - silently erasing what was just
// typed before the next keystroke arrives. Typing fast makes this worse, not better: more
// keystrokes land inside the same round-trip window. `useState(req.description)` seeded once per
// mount (this component's instance is stable across re-renders because its caller keys the row by
// req.id) decouples the visible text from that round trip entirely; onEditRequirement (which
// triggers the real mutate) only fires once, on blur, when the buffered value actually changed. ──
const RequirementRowEditor: React.FC<{
  req: Deliverable;
  saving: boolean;
  onEditRequirement: (reqId: string, patch: Partial<Deliverable>) => void;
  onRemoveRequirement: (reqId: string) => void;
}> = ({ req, saving, onEditRequirement, onRemoveRequirement }) => {
  const [description, setDescription] = useState(req.description);
  const [dueDate, setDueDate] = useState(req.dueDate || '');

  return (
    <div className="np-matrix-req-edit">
      <div className="np-matrix-req-edit-row">
        <input
          className="np-input" placeholder="Requirement description" value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={() => { if (description !== req.description) onEditRequirement(req.id, { description }); }}
        />
        <button type="button" className="np-icon-btn np-icon-btn-danger" title="Remove" disabled={saving} onClick={() => onRemoveRequirement(req.id)}>
          <i className="ri-delete-bin-line"></i>
        </button>
      </div>
      <input
        type="date" className="np-input" value={dueDate}
        onChange={(e) => setDueDate(e.target.value)}
        onBlur={() => { if (dueDate !== (req.dueDate || '')) onEditRequirement(req.id, { dueDate }); }}
      />
    </div>
  );
};

// ── Requirements × Teams matrix - replaces the old flat "Evidence Requirements" list as the
// Team Contributions card's primary surface. Presentational only (no useQuery/useMutation of its
// own): every edit is wired by the page through requirementsMutation, the same call the old
// standalone Manage editor used, so there is exactly one write path onto evidenceDeliverables. ──
const RequirementsMatrix: React.FC<{
  requirements: Deliverable[];
  teamData: Array<{ team: string; latest: Submission | undefined }>;
  editing: boolean;
  saving: boolean;
  onToggleAssignment: (reqId: string, team: string) => void;
  onEditRequirement: (reqId: string, patch: Partial<Deliverable>) => void;
  onAddRequirement: () => void;
  onRemoveRequirement: (reqId: string) => void;
  onJumpToTeam: (team: string) => void;
}> = ({ requirements, teamData, editing, saving, onToggleAssignment, onEditRequirement, onAddRequirement, onRemoveRequirement, onJumpToTeam }) => {
  const [openCell, setOpenCell] = useState<{ reqId: string; team: string; top: number; left: number; openUpward: boolean } | null>(null);

  // Switching into or out of Edit Assignments must never leave a stale view-mode popover
  // showing over the now-different cell rendering (the toggle checkbox vs. the status glyph).
  useEffect(() => { setOpenCell(null); }, [editing]);

  // Close on an outside click - a click on a DIFFERENT cell is still inside .np-matrix-cell, so
  // it's left alone here and handled by that cell's own onClick (which reassigns openCell).
  useEffect(() => {
    if (!openCell) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-matrix-popover]') || target.closest('.np-matrix-cell')) return;
      setOpenCell(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [openCell]);

  if (teamData.length === 0) {
    return (
      <div className="text-center np-sub py-4">
        <i className="ri-group-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
        No teams yet. Add one to start collecting evidence for this requirement.
      </div>
    );
  }

  if (!editing && requirements.length === 0) {
    return <div className="np-sub small py-2">No requirements listed yet — click Edit Assignments to add one.</div>;
  }

  const STATUS_LABEL: Record<CellStatus, string> = { done: 'Done', 'in-progress': 'In progress', 'not-started': 'Not started', 'not-applicable': 'Not applicable' };
  const STATUS_TONE_CLASS: Record<CellStatus, string> = { done: 'success', 'in-progress': 'warning', 'not-started': 'todo', 'not-applicable': 'na' };

  return (
    <div>
      {editing && (
        <div className="np-matrix-edit-banner">
          <i className="ri-information-line"></i>
          Click a cell to toggle whether that requirement applies to that team. Turning a cell off doesn't delete history already recorded.
        </div>
      )}
      <div className="np-matrix-wrap">
        <table className="np-matrix">
          <thead>
            <tr>
              <th className="np-matrix-req-col">Requirement</th>
              {teamData.map(({ team, latest }) => {
                const attn = attentionTone(latest);
                const applicable = requirements.filter((r) => appliesToTeam(r, team));
                const done = applicable.filter((r) => latest?.deliverables?.find((d) => d.id === r.id)?.status === 'Done').length;
                return (
                  <th key={team} className="np-matrix-team-col">
                    <div className="np-matrix-team-head">
                      <span className="np-matrix-avatar" style={{ background: avatarColorFor(team) }}>
                        {initials(team)}
                        {attn && <span className={`np-matrix-avatar-dot np-tone-${attn}`} title={attn === 'warning' ? "Awaiting your review" : 'Needs your attention'}></span>}
                      </span>
                      <span className="np-matrix-team-name">{team}</span>
                      {applicable.length > 0 && (
                        <span className={`np-matrix-rollup${done === applicable.length ? ' np-tone-success' : ''}`}><b>{done}</b>/{applicable.length}</span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {requirements.map((req) => {
              const applicableTeams = teamData.filter(({ team }) => appliesToTeam(req, team));
              const doneTeams = applicableTeams.filter(({ latest }) => latest?.deliverables?.find((d) => d.id === req.id)?.status === 'Done').length;
              const due = dueChip(req.dueDate);
              return (
                <tr key={req.id}>
                  <td className="np-matrix-req-cell">
                    {editing ? (
                      <RequirementRowEditor req={req} saving={saving} onEditRequirement={onEditRequirement} onRemoveRequirement={onRemoveRequirement} />
                    ) : (
                      <>
                        <span className="np-matrix-req-desc">{req.description || <span className="np-sub">Untitled requirement</span>}</span>
                        <div className="np-matrix-req-meta">
                          {due && <span className={`np-chip np-tone-${due.tone}`}>{due.label}</span>}
                          <span className={`np-matrix-rollup${doneTeams === applicableTeams.length && applicableTeams.length > 0 ? ' np-tone-success' : ''}`}><b>{doneTeams}</b>/{applicableTeams.length} teams</span>
                        </div>
                      </>
                    )}
                  </td>
                  {teamData.map(({ team, latest }) => {
                    const status = cellStatus(req, team, latest);
                    const applicable = status !== 'not-applicable';
                    const isOpen = !!openCell && openCell.reqId === req.id && openCell.team === team;
                    return (
                      <td
                        key={team}
                        className="np-matrix-cell"
                        onClick={(e) => {
                          if (saving) return;
                          if (editing) { onToggleAssignment(req.id, team); return; }
                          if (openCell && openCell.reqId === req.id && openCell.team === team) { setOpenCell(null); return; }
                          // position: fixed, computed from the cell's own viewport rect - not
                          // anchored via position: relative/absolute inside .np-matrix-wrap,
                          // which clips vertically as well as horizontally (overflow-x: auto on
                          // one axis computes the other to auto too, per the CSS Overflow spec),
                          // cutting off the popover for any row near the bottom of the grid.
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          const openUpward = window.innerHeight - rect.bottom < 200;
                          setOpenCell({ reqId: req.id, team, top: openUpward ? rect.top - 6 : rect.bottom + 6, left: rect.left + rect.width / 2, openUpward });
                        }}
                      >
                        {editing ? (
                          <span className={`np-matrix-toggle${applicable ? ' checked' : ''}`} title={applicable ? 'Applies to this team' : 'Not applicable to this team'}>
                            <i className="ri-check-line"></i>
                          </span>
                        ) : (
                          <span className={`np-matrix-status np-tone-${STATUS_TONE_CLASS[status]}`}>
                            {status === 'done' && <i className="ri-check-line"></i>}
                            {status === 'in-progress' && <i className="ri-record-circle-fill"></i>}
                            {status === 'not-applicable' && <i className="ri-subtract-line"></i>}
                          </span>
                        )}
                        {isOpen && openCell && createPortal(
                          <div
                            data-matrix-popover
                            className="np-popover"
                            style={{
                              position: 'fixed', top: openCell.top, left: openCell.left, width: '14rem',
                              transform: openCell.openUpward ? 'translate(-50%, -100%)' : 'translateX(-50%)',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="np-popover-head">
                              <span>{team}</span>
                              <button type="button" onClick={() => setOpenCell(null)}><i className="ri-close-line"></i></button>
                            </div>
                            <div style={{ padding: '0.375rem' }}>
                              <div className="np-sub" style={{ fontSize: '0.76rem' }}>{req.description}</div>
                              <div className="d-flex align-items-center justify-content-between mt-2">
                                <span className="np-hint">Status</span>
                                <span className={`np-chip np-tone-${status === 'done' ? 'success' : status === 'in-progress' ? 'warning' : 'neutral'}`}>{STATUS_LABEL[status]}</span>
                              </div>
                              {due && (
                                <div className="d-flex align-items-center justify-content-between mt-1">
                                  <span className="np-hint">Due</span>
                                  <span className="np-tabular" style={{ fontSize: '0.76rem' }}>{due.label}</span>
                                </div>
                              )}
                              <button type="button" className="np-link-action mt-2" onClick={() => { setOpenCell(null); onJumpToTeam(team); }}>
                                View team's round <i className="ri-arrow-right-line"></i>
                              </button>
                            </div>
                          </div>,
                          document.body
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {editing && (
              <tr className="np-matrix-add-row">
                <td colSpan={teamData.length + 1}>
                  <button type="button" className="np-link-action" disabled={saving} onClick={onAddRequirement}>
                    <i className="ri-add-line"></i>Add requirement
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!editing && requirements.length > 0 && (
        <div className="np-matrix-legend">
          <span className="np-matrix-legend-item"><span className="np-matrix-legend-glyph np-tone-success"><i className="ri-check-line"></i></span>Done</span>
          <span className="np-matrix-legend-item"><span className="np-matrix-legend-glyph np-tone-warning"><i className="ri-record-circle-fill"></i></span>In progress</span>
          <span className="np-matrix-legend-item"><span className="np-matrix-legend-glyph np-tone-todo"></span>Not started</span>
          <span className="np-matrix-legend-item"><span className="np-matrix-legend-glyph np-tone-na"><i className="ri-subtract-line"></i></span>Not applicable</span>
          <span className="np-matrix-legend-item"><span className="np-matrix-legend-dot"></span>Team avatar dot = their current round needs your attention</span>
        </div>
      )}
    </div>
  );
};


// ── One team's row in the Team Workflow accordion - collapsed summary + inline expand ──────────
const TeamRow: React.FC<{
  evidenceId: string;
  teamName: string;
  pendingDepartmentId?: string;
  latest: Submission | undefined;
  earlier: Submission[];
  isAdmin: boolean;
  expanded: boolean;
  highlighted: boolean;
  people: Person[];
  /** The evidence item's single next_due_date - Evidence has one schedule, not one per team. */
  nextDueDate?: string;
  /** Current Evidence Requirements count - the denominator a team's first submission starts from. */
  requirementCount: number;
  onToggle: () => void;
  /** Jump from a round's RACI chips to the evidence-level RACI section. */
  onViewFullRaci: () => void;
}> = ({ evidenceId, teamName, pendingDepartmentId, latest, earlier, isAdmin, expanded, highlighted, people, nextDueDate, requirementCount, onToggle, onViewFullRaci }) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [reviewNote, setReviewNote] = useState('');
  const [strengthOverride, setStrengthOverride] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showEarlier, setShowEarlier] = useState(false);
  const [roundDeliverablesOpen, setRoundDeliverablesOpen] = useState(false);
  const [selectedRoundId, setSelectedRoundId] = useState<string | undefined>(latest?.submissionId);
  const [commentText, setCommentText] = useState('');

  const round = [latest, ...earlier].find((s) => s?.submissionId === selectedRoundId) || latest;
  // Only the comments: the submitted fact lives on the round-history entry and the decision in the
  // decided strip, so each fact renders in exactly one place inside the expanded row.
  const roundComments = round ? buildTimeline(round).filter((e) => e.kind === 'comment') : [];

  const { data: roundRaci = [] } = useQuery<any[]>({
    queryKey: ['raci', 'submission', round?.submissionId],
    queryFn: () => raciApi.getByArtefact('submission', round!.submissionId),
    enabled: expanded && !!round?.submissionId,
  });
  const { data: roundDocs = [] } = useQuery<any[]>({
    queryKey: ['documents', 'evidence', round?.submissionId],
    queryFn: () => documentsApi.getAll('evidence', round!.submissionId),
    enabled: expanded && !!round?.submissionId,
  });

  const reviewMutation = useMutation({
    mutationFn: (action: 'Accepted' | 'Rejected' | 'Clarification Needed') =>
      evidenceSubmissionsApi.review(evidenceId, round!.submissionId, { action, reviewNote, strengthOverride: strengthOverride || undefined }),
    onSuccess: (_d, action) => {
      toast[action === 'Accepted' ? 'success' : 'warning'](action === 'Clarification Needed' ? 'Clarification requested.' : `Submission ${action.toLowerCase()}.`);
      setReviewNote('');
      queryClient.invalidateQueries({ queryKey: ['evidenceSubmissions', evidenceId] });
    },
    onError: (e: any) => toast.error(e.message || 'Review failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => evidenceSubmissionsApi.delete(evidenceId, round!.submissionId),
    onSuccess: () => {
      toast.success('Round deleted.');
      queryClient.invalidateQueries({ queryKey: ['evidenceSubmissions', evidenceId] });
    },
    onError: (e: any) => toast.error(e.message || 'Delete failed'),
  });

  const deliverablesMutation = useMutation({
    mutationFn: (deliverables: Deliverable[]) => evidenceSubmissionsApi.update(evidenceId, round!.submissionId, { deliverables }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['evidenceSubmissions', evidenceId] }),
    onError: (e: any) => toast.error(e.message || 'Failed to save deliverables'),
  });

  const commentMutation = useMutation({
    mutationFn: () => evidenceSubmissionsApi.comment(evidenceId, round!.submissionId, commentText.trim()),
    onSuccess: () => {
      setCommentText('');
      queryClient.invalidateQueries({ queryKey: ['evidenceSubmissions', evidenceId] });
    },
    onError: (e: any) => toast.error(e.message || 'Failed to post comment'),
  });

  if (!latest) {
    // No submission at all yet for this team - a roster-only row, no history to show.
    const due = dueChip(nextDueDate);
    return (
      <div className={`np-team-item${expanded ? ' open' : ''}${highlighted ? ' np-highlight-pulse' : ''}`}>
        <button type="button" className="np-team-item-head" onClick={onToggle}>
          <span className="np-matrix-avatar" style={{ background: avatarColorFor(teamName) }}>{initials(teamName)}</span>
          <span className="np-row-title" style={{ flex: '1 1 8rem', minWidth: 0 }}>{teamName}</span>
          <span className="np-chip np-tone-danger">Not submitted</span>
          {due ? <span className={`np-chip np-tone-${due.tone}`}>{due.label}</span> : null}
          <FractionRing done={0} total={requirementCount} tone="danger" />
          <i className={`ri-arrow-right-s-line np-ledger-chev${expanded ? ' open' : ''}`}></i>
        </button>
        {expanded && (
          <div className="np-team-item-body">
            <div className="np-hint mb-2" style={{ marginTop: 0 }}>
              <i className="ri-information-line me-1"></i>
              {requirementCount > 0
                ? `Pre-filled from the ${requirementCount} Evidence Requirement${requirementCount === 1 ? '' : 's'} assigned to this team above — add team-specific items after.`
                : 'No Evidence Requirements are assigned to this team yet — this submission will start with an empty deliverables list.'}
            </div>
            <NewSubmissionComposer evidenceId={evidenceId} teamName={teamName} teamDepartmentId={pendingDepartmentId} onCreated={onToggle} />
          </div>
        )}
      </div>
    );
  }

  const total = latest.deliverables?.length || 0;
  const done = (latest.deliverables || []).filter((d) => d.status === 'Done').length;
  const canReview = latest.status !== 'Accepted' && latest.status !== 'Rejected';
  const tone = STATUS_TONE[latest.status] || 'neutral';
  const timelinessTone = latest.timeliness === 'Late' ? 'warning' : 'neutral';

  return (
    <div className={`np-team-item${expanded ? ' open' : ''}${highlighted ? ' np-highlight-pulse' : ''}`}>
      <button type="button" className="np-team-item-head" onClick={onToggle}>
        <span className="np-matrix-avatar" style={{ background: avatarColorFor(teamName) }}>
          {initials(teamName)}
          {attentionTone(latest) && <span className={`np-matrix-avatar-dot np-tone-${attentionTone(latest)}`}></span>}
        </span>
        <span style={{ flex: '1 1 8rem', minWidth: 0 }}>
          <span className="np-row-title d-block">{teamName}</span>
          <span className="np-hint">Round {[latest, ...earlier].length} &middot; {fmtRelative(latest.submittedAt)}</span>
        </span>
        <span className={`np-chip np-tone-${tone}`}>{latest.status === 'Submitted' || latest.status === 'In Review' ? 'Awaiting your review' : latest.status}</span>
        <span className={`np-chip np-tone-${timelinessTone}`}>{latest.timeliness}</span>
        <FractionRing done={done} total={total} tone={done === total && total > 0 ? 'success' : 'warning'} />
        <i className={`ri-arrow-right-s-line np-ledger-chev${expanded ? ' open' : ''}`}></i>
      </button>
      {expanded && (
        <div className="np-team-item-body">
          <div className="np-row2">
            {/* Left: round history, this round's deliverables, and the decision (moved up ahead
                of Files/RACI/Comments so the reviewer's own action is in the same column as the
                history that leads to it). */}
            <div>
              <div className="mb-3">
                <span className="eyebrow-label">Round history</span>
                {[latest, ...earlier].map((s, i) => {
                  const isSelected = s.submissionId === round?.submissionId;
                  const isSettled = s.submissionId !== latest.submissionId;
                  const tone = STATUS_TONE[s.status] || 'neutral';
                  return (
                    <div
                      key={s.submissionId}
                      className="np-timeline-item"
                      style={{ cursor: 'pointer', opacity: isSelected ? 1 : 0.85 }}
                      onClick={() => setSelectedRoundId(s.submissionId)}
                    >
                      <span className="np-timeline-dot" style={{ background: isSettled ? 'var(--bs-border-color)' : `var(--bs-${bsTone(tone)})` }}></span>
                      <div className="np-timeline-title" style={{ color: isSettled ? 'var(--bs-secondary-color, var(--bs-body-color))' : `var(--bs-${bsTone(tone)})`, textDecoration: isSelected ? 'underline' : 'none' }}>
                        Round {[latest, ...earlier].length - i} — {s.status}{!isSettled && (s.status === 'Submitted' || s.status === 'In Review') ? ', awaiting decision' : ''}
                      </div>
                      <div className="np-timeline-meta">
                        {s.submittedByName || s.submittedBy} &middot; {fmtRelative(s.submittedAt)}
                        {isSettled ? ' · settled' : ''}
                      </div>
                      {isSelected && s.notes && <div className="np-timeline-meta" style={{ color: 'var(--bs-body-color)' }}>&ldquo;{s.notes}&rdquo;</div>}
                    </div>
                  );
                })}
              </div>

              {round && (
                <>
                  <div className="mb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <strong style={{ fontSize: 'var(--np-text-sm)' }}>Deliverables (this round)</strong>
                      <button type="button" className="np-btn np-btn-ghost np-btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setRoundDeliverablesOpen((v) => !v)}>
                        <i className="ri-settings-3-line"></i>{roundDeliverablesOpen ? 'Done' : 'Manage'}
                      </button>
                    </div>
                    {/* Read-only checklist by default, same Manage/Done idiom as the Requirements
                        Matrix and RACI on this page - the always-live editor PUT + refetched on
                        every keystroke. */}
                    {roundDeliverablesOpen ? (
                      <DeliverablesEditor value={round.deliverables || []} onChange={(next) => deliverablesMutation.mutate(next)} compact />
                    ) : (round.deliverables || []).length === 0 ? (
                      <span className="np-sub small">No deliverables recorded for this round.</span>
                    ) : (
                      (round.deliverables || []).map((d) => {
                        const isDone = d.status === 'Done';
                        return (
                          <div key={d.id} className="np-deliv-row">
                            <i className={isDone ? 'ri-checkbox-circle-fill text-success' : 'ri-checkbox-blank-circle-line np-sub'}></i>
                            <span className={isDone ? '' : 'np-sub'}>{d.description}</span>
                            {d.status === 'In Progress' && <span className="np-chip np-tone-warning ms-auto">In progress</span>}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {canReview && round.submissionId === latest.submissionId && (
                    <div className="p-3" style={{ border: '1px solid var(--bs-warning-border-subtle)', background: 'var(--bs-warning-bg-subtle)', borderRadius: 'var(--np-radius)' }}>
                      <div className="fw-semibold small mb-2"><i className="ri-check-double-line me-1"></i>Your decision</div>
                      <label className="d-block small fw-semibold mb-1">
                        Review note <span className="text-danger">*</span>{' '}
                        <span className="np-hint fw-normal">— required for Accept, Reject, or Clarification alike</span>
                      </label>
                      <textarea
                        className="np-input mb-2"
                        rows={2}
                        placeholder="What did you check? Why this decision?"
                        value={reviewNote}
                        onChange={(e) => setReviewNote(e.target.value)}
                      />
                      <div className="np-field" style={{ marginBottom: '0.75rem' }}>
                        <label className="d-block small fw-semibold mb-1">Strength / Assurance</label>
                        <select className="np-select" value={strengthOverride} onChange={(e) => setStrengthOverride(e.target.value)}>
                          <option value="">— keep evidence strength —</option>
                          {STRENGTH_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                        <div className="np-hint mt-1">Recorded for the audit trail. Does not currently affect this Control's Effectiveness score.</div>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <button className="np-btn np-btn-sm np-btn-success" disabled={reviewMutation.isPending || !reviewNote.trim()} onClick={() => reviewMutation.mutate('Accepted')}>
                          <i className="ri-checkbox-circle-line"></i>Accept
                        </button>
                        <button className="np-btn np-btn-sm np-btn-danger" disabled={reviewMutation.isPending || !reviewNote.trim()} onClick={() => reviewMutation.mutate('Rejected')}>
                          <i className="ri-close-circle-line"></i>Reject
                        </button>
                        <button className="np-btn np-btn-sm np-btn-warning" disabled={reviewMutation.isPending || !reviewNote.trim()} onClick={() => reviewMutation.mutate('Clarification Needed')}>
                          <i className="ri-question-answer-line"></i>Request Clarification
                        </button>
                      </div>
                    </div>
                  )}

                  {(round.status === 'Accepted' || round.status === 'Rejected' || round.status === 'Clarification Needed') && (
                    <div className="p-3" style={{ border: `1px solid var(--bs-${STATUS_TONE[round.status]}-border-subtle, var(--np-border))`, background: `var(--bs-${STATUS_TONE[round.status]}-bg-subtle, var(--np-surface))`, borderRadius: 'var(--np-radius)' }}>
                      <div className="d-flex align-items-start gap-2">
                        <i className={`ri-${round.status === 'Accepted' ? 'checkbox-circle' : round.status === 'Rejected' ? 'close-circle' : 'question-answer'}-line text-${STATUS_TONE[round.status]}`}></i>
                        <div>
                          <strong>{round.status} by {round.reviewedByName || round.reviewedBy} · {fmtRelative(round.reviewedAt)}</strong>
                          {round.reviewNote && <div className="mt-1">&ldquo;{round.reviewNote}&rdquo;</div>}
                          {round.strengthOverride && <span className={`np-chip np-tone-${STATUS_TONE[round.status]} mt-2 d-inline-block`}>Strength: {round.strengthOverride}</span>}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right: files, this evidence's team RACI, comments - same `round &&` guard as the
                left column (round is always defined by this point at runtime - the [latest,
                ...earlier].find(...) || latest fallback can't actually miss - but TS still sees
                Submission | undefined here, same as the mutations above that resolve it with `!`). */}
            {round && (
            <div>
              <div className="mb-3">
                <span className="eyebrow-label">Files</span>
                {roundDocs.length === 0 ? (
                  <span className="np-sub small">No files attached.</span>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {roundDocs.map((doc: any) => (
                      <span key={doc.documentId} className="np-chip np-tone-neutral" style={{ cursor: 'pointer' }} onClick={() => documentsApi.download(doc.documentId, doc.fileName)}>
                        <i className={fileIcon(doc.fileType)}></i>{doc.fileName} <span className="np-sub">({fmtSize(doc.fileSize)})</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <span className="eyebrow-label">Team RACI</span>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {roundRaci.length === 0 && <span className="np-sub small">No one assigned yet.</span>}
                  {roundRaci.map((r: any) => {
                    const personName = people.find((p) => p.personId === r.personId)?.personName || r.personId;
                    return (
                    <span key={r.raciId} className="np-chip np-tone-neutral">
                      <span className="avatar avatar-xs bg-label-primary rounded-circle me-1" style={{ width: '1.1rem', height: '1.1rem', fontSize: '0.55rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{initials(personName)}</span>
                      {personName} · {r.role}
                    </span>
                    );
                  })}
                  <button type="button" className="np-link-action" onClick={onViewFullRaci}>View full table →</button>
                </div>
                <details className="mt-2">
                  <summary className="np-hint" style={{ cursor: 'pointer' }}>Manage this round's RACI</summary>
                  <div className="mt-2">
                    <RaciAssignments artefactId={round.submissionId} artefactType="submission" compact />
                  </div>
                </details>
              </div>

              <div className="mb-3">
                <span className="eyebrow-label">Comments</span>
                {roundComments.length === 0 ? (
                  <span className="np-sub small d-block mb-2">No comments yet.</span>
                ) : (
                  // Own wrapper so .np-timeline-item:last-child { padding-bottom: 0 } applies -
                  // the comment input row below is otherwise the last sibling.
                  <div className="mb-2">
                    {roundComments.map((entry, i) => (
                      <div key={i} className="np-timeline-item">
                        <span className="np-timeline-dot" style={{ background: `var(--bs-${bsTone(entry.tone)})` }}></span>
                        <div className="np-timeline-title">{entry.label} <span className="np-hint fw-normal">· {entry.author} · {fmtRelative(entry.at)}</span></div>
                        {entry.body && <div style={{ fontSize: '0.82rem' }}>{entry.body}</div>}
                      </div>
                    ))}
                  </div>
                )}
                <div className="d-flex align-items-center gap-2 mt-2">
                  <input
                    className="np-input"
                    placeholder="Leave a comment…"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && commentText.trim() && !commentMutation.isPending) commentMutation.mutate(); }}
                    disabled={commentMutation.isPending}
                  />
                  <button className="np-btn np-btn-ghost np-btn-sm" disabled={commentMutation.isPending || !commentText.trim()} onClick={() => commentMutation.mutate()}>
                    {commentMutation.isPending ? <span className="spinner-border spinner-border-sm"></span> : <i className="ri-send-plane-line"></i>}
                  </button>
                </div>
              </div>
            </div>
            )}
          </div>

          {isAdmin && round && (
            <div className="mt-1 text-end">
              {!confirmDelete ? (
                <button className="np-btn np-btn-sm np-btn-ghost-danger" onClick={() => setConfirmDelete(true)}>
                  <i className="ri-delete-bin-line"></i>Delete this round
                </button>
              ) : (
                <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap">
                  <span className="small text-danger fw-semibold">
                    Deletes {round.comments?.length || 0} comment{(round.comments?.length || 0) === 1 ? '' : 's'}, {round.deliverables?.length || 0} deliverable{(round.deliverables?.length || 0) === 1 ? '' : 's'}, {roundDocs.length} file{roundDocs.length === 1 ? '' : 's'}, and {roundRaci.length} RACI assignment{roundRaci.length === 1 ? '' : 's'}. This cannot be undone.
                  </span>
                  <button className="np-btn np-btn-sm np-btn-danger" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
                    {deleteMutation.isPending ? <span className="spinner-border spinner-border-sm"></span> : 'Yes, delete'}
                  </button>
                  <button className="np-btn np-btn-sm np-btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
                </div>
              )}
            </div>
          )}

          {/* Resubmission guardrail: a live composer only replaces this once the user
              explicitly picks one of the two paths below - never live by default while a
              round is still undecided. */}
          {canReview && !showEarlier && (
            <div className="np-hint mt-3 p-2" style={{ background: 'var(--np-surface)', borderRadius: '0.5rem', border: '1px solid var(--np-border)' }}>
              A submission from this team is already awaiting review.{' '}
              <button type="button" className="np-link-action" onClick={() => setShowEarlier(true)}>Add a supplementary submission instead →</button>
            </div>
          )}
          {(!canReview || showEarlier) && (
            <div className="mt-3">
              <strong className="d-block mb-2" style={{ fontSize: 'var(--np-text-sm)' }}>
                {canReview ? 'New supplementary submission' : 'New submission'}
              </strong>
              <NewSubmissionComposer evidenceId={evidenceId} teamName={teamName} teamDepartmentId={latest.teamDepartmentId} onCreated={() => setShowEarlier(false)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Minimal submission composer (file upload + note + deliverables), used both for a brand
// new team and for a supplementary round on an already-decided team. ──
const NewSubmissionComposer: React.FC<{ evidenceId: string; teamName: string; teamDepartmentId?: string; onCreated?: () => void }> = ({
  evidenceId, teamName, teamDepartmentId, onCreated,
}) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [notes, setNotes] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const createMutation = useMutation({
    mutationFn: () => evidenceSubmissionsApi.create(evidenceId, { notes, teamName, teamDepartmentId }),
    onSuccess: async (data: any) => {
      const submissionId: string = data?.submission?.submissionId;
      if (submissionId && selectedFiles.length > 0) {
        setUploading(true);
        for (const file of selectedFiles) {
          try {
            await documentsApi.upload('evidence', submissionId, file);
          } catch (err: any) {
            toast.error(`Failed to upload ${file.name}: ${err.message}`);
          }
        }
        setUploading(false);
      }
      toast.success('Submission recorded.');
      queryClient.invalidateQueries({ queryKey: ['evidenceSubmissions', evidenceId] });
      setNotes('');
      setSelectedFiles([]);
      onCreated?.();
    },
    onError: (e: any) => toast.error(e.message || 'Failed to submit'),
  });

  const busy = createMutation.isPending || uploading;

  return (
    <div>
      <Dropzone
        onFilesSelected={(files) => setSelectedFiles((prev) => {
          const names = new Set(prev.map((f) => f.name));
          return [...prev, ...files.filter((f) => !names.has(f.name))];
        })}
        accept=".pdf,.png,.jpg,.jpeg,.xlsx,.docx,.doc,.xls,.csv,.txt"
        disabled={busy}
        hint="PDF, PNG, JPG, XLSX, DOCX, CSV, TXT · max 25 MB each"
      />
      {selectedFiles.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {selectedFiles.map((file) => (
            <span key={file.name} className="np-chip np-tone-neutral">
              <i className={fileIcon(file.type)}></i>{file.name}
              <i className="ri-close-line ms-1" style={{ cursor: 'pointer' }} onClick={() => setSelectedFiles((prev) => prev.filter((f) => f.name !== file.name))}></i>
            </span>
          ))}
        </div>
      )}
      <textarea className="np-input mt-2" rows={2} placeholder="Describe what evidence you're submitting…" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div className="mt-2">
        <button className="np-btn np-btn-primary np-btn-sm" onClick={() => createMutation.mutate()} disabled={busy}>
          {busy ? <span className="spinner-border spinner-border-sm"></span> : <><i className="ri-send-plane-line"></i>Submit</>}
        </button>
      </div>
    </div>
  );
};

// ── Add Team modal ───────────────────────────────────────────────────────────────────────────
const AddTeamModal: React.FC<{ existingNames: string[]; onClose: () => void; onAdd: (name: string, departmentId?: string) => void }> = ({
  existingNames, onClose, onAdd,
}) => {
  const { data: departments = [] } = useQuery<Department[]>({ queryKey: ['departments'], queryFn: departmentsApi.getAll });
  const [departmentId, setDepartmentId] = useState('');
  const [custom, setCustom] = useState(false);
  const [customName, setCustomName] = useState('');

  const selectedDept = (departments as Department[]).find((d) => d.departmentId === departmentId);
  const finalName = custom ? customName.trim() : (selectedDept?.name || '');
  const isDuplicate = finalName && existingNames.some((n) => n.toLowerCase() === finalName.toLowerCase());

  return (
    <NimbusModal centered size="sm">
      <NimbusModalContent
        title="Add a contributing team"
        onClose={onClose}
        footer={
          <>
            <button className="np-btn np-btn-ghost" onClick={onClose}>Cancel</button>
            <button className="np-btn np-btn-primary" disabled={!finalName || !!isDuplicate} onClick={() => onAdd(finalName, custom ? undefined : departmentId)}>
              Add Team
            </button>
          </>
        }
      >
        <div className="np-field">
          <label>Team / Department</label>
          <select className="np-select" value={custom ? '__custom__' : departmentId} onChange={(e) => {
            if (e.target.value === '__custom__') { setCustom(true); setDepartmentId(''); }
            else { setCustom(false); setDepartmentId(e.target.value); }
          }}>
            <option value="">Select a department…</option>
            {(departments as Department[]).map((d) => <option key={d.departmentId} value={d.departmentId}>{d.name}</option>)}
            <option value="__custom__">Use a custom label instead</option>
          </select>
        </div>
        {custom && (
          <div className="np-field">
            <input className="np-input mb-2" placeholder="e.g. External Audit Support" value={customName} onChange={(e) => setCustomName(e.target.value)} />
            <span className="np-hint">This row will be visibly tagged </span>
            <span className="np-chip np-tone-neutral">Custom</span>
            <span className="np-hint"> so it's never mistaken for a real department.</span>
          </div>
        )}
        {isDuplicate && <div className="np-banner np-tone-warning"><i className="ri-error-warning-line"></i>A team named "{finalName}" already exists on this evidence item.</div>}
      </NimbusModalContent>
    </NimbusModal>
  );
};

// ── Health & Rollup computation ──────────────────────────────────────────────────────────────
type Health = { tone: 'success' | 'warning' | 'danger' | 'neutral'; label: string; detail: string };

function computeHealth(
  evidenceStatus: string | undefined,
  scheduleStatus: string | undefined,
  nextDueDate: string | undefined,
  teamLatest: Array<{ team: string; sub: Submission | undefined }>,
): Health {
  if (String(evidenceStatus || '').toLowerCase() === 'descoped') {
    return { tone: 'neutral', label: 'Descoped', detail: 'Review tracking suspended for this requirement.' };
  }

  // Zero teams is not the same thing as "every team accepted" - falling through to the
  // Healthy branch below with an empty list would (and, confirmed live, did) claim "every
  // team's current round is Accepted" while the very same banner's own Teams tile reads 0.
  if (teamLatest.length === 0) {
    return { tone: 'neutral', label: 'Not Started', detail: 'No team is contributing to this requirement yet - add one below.' };
  }

  const rejected = teamLatest.filter((t) => t.sub?.status === 'Rejected');
  const notSubmitted = teamLatest.filter((t) => !t.sub);
  // 'In Review' is a real, first-class pending status elsewhere in this exact codebase
  // (CollectionScheduleCard, the backend's own pending-submissions query) - it must be treated
  // the same as 'Submitted', not silently excluded from every rollup here.
  const awaiting = teamLatest.filter((t) => t.sub && (t.sub.status === 'Submitted' || t.sub.status === 'In Review' || t.sub.status === 'Clarification Needed'));
  // Same calendar-day definition daysUntil/dueChip use (both normalised to local midnight), so the
  // Health banner and the ledger's per-row due-date chip can never disagree about whether "today"
  // counts as overdue - a raw millisecond comparison against Date.now() made "due today" read as
  // overdue here while the row chip (and this function's own "(due today)" wording below) read it
  // as merely due, for the entirety of the due date.
  const daysToDue = daysUntil(nextDueDate);
  const overdueByDate = daysToDue !== null && daysToDue < 0;

  if (rejected.length > 0 || scheduleStatus === 'Overdue' || (notSubmitted.length > 0 && overdueByDate)) {
    const bits: string[] = [];
    if (rejected.length) bits.push(`${rejected.map((t) => t.team).join(', ')} ${rejected.length === 1 ? 'was' : 'were'} rejected`);
    if (notSubmitted.length && overdueByDate) bits.push(`${notSubmitted.map((t) => t.team).join(', ')} ${notSubmitted.length === 1 ? 'hasn’t' : 'haven’t'} submitted (overdue)`);
    // A rejection can co-exist with teams still awaiting decision or not yet due - list them too,
    // so this branch's detail text never silently drops teams the adjacent mini-stat tiles count.
    if (awaiting.length) bits.push(`${awaiting.map((t) => t.team).join(', ')} ${awaiting.length === 1 ? 'has' : 'have'} a submission awaiting your decision`);
    if (notSubmitted.length && !overdueByDate) {
      const due = daysToDue === null ? '' : daysToDue === 0 ? ' (due today)' : ` (due in ${daysToDue} day${daysToDue === 1 ? '' : 's'})`;
      bits.push(`${notSubmitted.map((t) => t.team).join(', ')} ${notSubmitted.length === 1 ? 'hasn’t' : 'haven’t'} submitted yet${due}`);
    }
    return { tone: 'danger', label: 'At Risk', detail: bits.join(', and ') || 'This requirement needs attention.' };
  }

  // Every team must have an Accepted latest round (and nobody simply missing) for this to be
  // Healthy - a team that hasn't submitted yet, even before its due date, is never "Healthy":
  // claiming "every team's current round is Accepted" while a team has no round at all is a
  // directly self-contradicting status next to this same banner's own "Not submitted" count.
  if (awaiting.length > 0 || notSubmitted.length > 0 || scheduleStatus === 'Due Soon') {
    const bits: string[] = [];
    if (awaiting.length) bits.push(`${awaiting.map((t) => t.team).join(', ')} ${awaiting.length === 1 ? 'has' : 'have'} a submission awaiting your decision`);
    if (notSubmitted.length) {
      // The overdue + not-submitted case already routed to the danger branch above with its own
      // "(overdue)" suffix, so a negative day count here only arises without a due date.
      const due = daysToDue === null || daysToDue < 0 ? '' : daysToDue === 0 ? ' (due today)' : ` (due in ${daysToDue} day${daysToDue === 1 ? '' : 's'})`;
      bits.push(`${notSubmitted.map((t) => t.team).join(', ')} ${notSubmitted.length === 1 ? 'hasn’t' : 'haven’t'} submitted yet${due}`);
    }
    // Reached with an empty bits[] when every team is Accepted and only the schedule itself is Due
    // Soon - the generic fallback below reads "Some submissions are awaiting review" while the
    // Awaiting-review tile reads 0 and no review-queue button is shown, which is misleading.
    if (!bits.length && scheduleStatus === 'Due Soon') {
      const due = daysToDue === null ? '' : daysToDue === 0 ? ' today' : ` in ${daysToDue} day${daysToDue === 1 ? '' : 's'}`;
      bits.push(`Next collection is due${due}`);
    }
    return { tone: 'warning', label: 'Needs Review', detail: bits.join(', and ') || 'Some submissions are awaiting review.' };
  }

  return { tone: 'success', label: 'Healthy', detail: 'Every team’s current round is Accepted and the schedule is on track.' };
}

// ── Field sanitization (same shape RegisterItemDetail uses) ───────────────────────────────────
// A pure function rather than an effect body so Cancel can rebuild fieldValues from the server
// item too - the populate effect only re-runs when evidenceItem itself changes, which discarding
// local edits never does.
const EVIDENCE_METADATA_KEYS = ['evidenceId', 'tenantId', 'registerId', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy', 'fields', 'deliverables', 'linked_artefacts'];
function sanitizeEvidenceItem(evidenceItem: any): FieldValues {
  const sanitized: FieldValues = {};
  if (evidenceItem.fields && typeof evidenceItem.fields === 'object') {
    Object.keys(evidenceItem.fields).forEach((key) => {
      const val = evidenceItem.fields[key];
      sanitized[key] = val && typeof val === 'object' && 'value' in val ? val.value : val;
    });
  }
  Object.keys(evidenceItem).forEach((key) => {
    if (EVIDENCE_METADATA_KEYS.includes(key)) return;
    const val = evidenceItem[key];
    sanitized[key] = val && typeof val === 'object' && 'value' in val ? val.value : val;
  });
  if (!sanitized['evidence_date_uploaded'] && evidenceItem.createdAt) {
    sanitized['evidence_date_uploaded'] = String(evidenceItem.createdAt).slice(0, 10);
  }
  return sanitized;
}

// ── Main page ─────────────────────────────────────────────────────────────────────────────────
export const EvidenceRecordDetail: React.FC = () => {
  const { id: itemId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const registerId = searchParams.get('registerId') || '';
  const { user, getTenantId } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const queryClient = useQueryClient();
  const toast = useToast();

  const isNew = !itemId;
  const [isEditing, setIsEditing] = useState(isNew);
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const [invalidFieldIds, setInvalidFieldIds] = useState<Set<string>>(new Set());
  // Several team rows can stay open at once so a reviewer can compare teams - each TeamRow owns
  // its own state and gates its per-round queries on `expanded`, so nothing needs single-open.
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(() => new Set());
  const toggleTeam = (team: string) => setExpandedTeams((prev) => { const next = new Set(prev); if (next.has(team)) next.delete(team); else next.add(team); return next; });
  const openTeam = (team: string) => setExpandedTeams((prev) => (prev.has(team) ? prev : new Set(prev).add(team)));
  const [highlightedTeam, setHighlightedTeam] = useState<string | null>(null);
  const [addTeamOpen, setAddTeamOpen] = useState(false);
  const [raciManageOpen, setRaciManageOpen] = useState(false);
  const { sync: syncRaciFromControls, isSyncing: raciSyncing } = useSyncRaciFromControls(itemId, 'evidence');
  const [artefactsManageOpen, setArtefactsManageOpen] = useState(false);
  const [artefactQuery, setArtefactQuery] = useState('');
  const [artefactTypeFilter, setArtefactTypeFilter] = useState('');
  const [pendingTeams, setPendingTeams] = useState<Array<{ name: string; departmentId?: string }>>([]);
  // Real exclusive tabs (the same .np-tabs/activeTab pattern ControlDetail.tsx/RiskDetail.tsx/
  // RegisterItemDetail.tsx already use) - only one panel is mounted at a time, so there's no
  // sticky rail anymore (a "scroll to section" rail has no meaning once sections aren't all on
  // screen; none of those three pages have one alongside their own tabs either).
  const [activeTab, setActiveTab] = useState<'overview' | 'raci' | 'attachments' | 'artefacts'>('overview');
  // Cross-tab jumps (the header Edit button, the review-queue mini-stats, RACI's own "view all"
  // hint) land on a specific card inside the Overview tab. Switching activeTab and that card
  // mounting happen in the same render pass as the state update, so the ref isn't attached yet at
  // the moment a jump fires - pendingScrollTarget + the effect below resolve it one tick later.
  const [pendingScrollTarget, setPendingScrollTarget] = useState<'details' | 'team' | 'effectiveness' | null>(null);
  const overviewRefs = {
    effectiveness: useRef<HTMLElement>(null),
    details: useRef<HTMLElement>(null),
    team: useRef<HTMLElement>(null),
  };
  const goToOverview = (target?: 'details' | 'team' | 'effectiveness') => {
    setActiveTab('overview');
    if (target) setPendingScrollTarget(target);
  };
  useEffect(() => {
    if (activeTab !== 'overview' || !pendingScrollTarget) return;
    const ref = overviewRefs[pendingScrollTarget];
    requestAnimationFrame(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    setPendingScrollTarget(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pendingScrollTarget]);
  // Requirements Matrix: 'owner' is the compliance-owner console (the matrix + Team Workflow
  // accordion); 'team' is a manual, self-service simplified view - NOT an access boundary (there
  // is no user-to-team identity anywhere in this data model - teamName is free-typed and useAuth
  // only exposes isAdmin), anyone can flip to it and pick any team.
  const [teamContribView, setTeamContribView] = useState<'owner' | 'team'>('owner');
  const [pickedTeam, setPickedTeam] = useState<string | null>(null);
  const [editingAssignments, setEditingAssignments] = useState(false);

  const { data: item, isLoading: itemLoading } = useQuery({
    queryKey: ['evidence', itemId],
    queryFn: () => evidenceApi.getOne(itemId!),
    enabled: !!itemId,
  });
  const evidenceItem = (item as any)?.evidence ?? item;

  const { data: fieldConfigs = [] } = useQuery({
    queryKey: ['fieldConfigs', 'evidence', registerId],
    queryFn: () => registerConfigApi.getFieldConfigs('evidence', registerId),
    enabled: !!registerId,
  });

  const { data: submissionsData } = useQuery<any>({
    queryKey: ['evidenceSubmissions', itemId],
    queryFn: () => evidenceSubmissionsApi.list(itemId!),
    enabled: !!itemId,
  });
  const submissions: Submission[] = ((submissionsData?.submissions ?? []) as Submission[])
    .slice()
    .sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));

  // Team-round RACI count for the RACI card head/hint. Same queryKey as TeamRow's roundRaci query,
  // so expanding a row reuses this cache entry instead of refetching.
  const teamRoundRaciQueries = useQueries({
    queries: submissions.map((s) => ({
      queryKey: ['raci', 'submission', s.submissionId],
      queryFn: () => raciApi.getByArtefact('submission', s.submissionId),
      enabled: !!s.submissionId,
    })),
  });
  const teamRoundRaciCount = teamRoundRaciQueries.reduce((n, q) => n + (((q.data as any[]) || []).length), 0);

  const { data: evidenceRaci = [] } = useQuery<any[]>({
    queryKey: ['raci', 'evidence', itemId],
    queryFn: () => raciApi.getByArtefact('evidence', itemId!),
    enabled: !!itemId,
  });
  // Same key DocumentManager uses for this entity, so the fetch is deduped and DocumentManager's
  // own upload/delete invalidations refresh the rail and card-head counts too.
  const { data: evidenceDocs = [] } = useQuery<any[]>({
    queryKey: ['documents', 'evidence', itemId],
    queryFn: () => documentsApi.getAll('evidence', itemId!),
    enabled: !!itemId,
  });
  const { data: people = [] } = useQuery<Person[]>({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const { data: assets = [] } = useQuery<Asset[]>({ queryKey: ['assets'], queryFn: assetsApi.getAll });
  const { data: departments = [] } = useQuery<Department[]>({ queryKey: ['departments'], queryFn: departmentsApi.getAll });

  const { data: linksData } = useQuery<any>({
    queryKey: ['linkedArtefacts', itemId, 'evidence'],
    queryFn: () => linkedArtefactsApi.getLinks(itemId!, 'evidence'),
    enabled: !!itemId,
  });
  const links: any[] = (linksData as any)?.links || [];
  const linkedFrameworks = links.filter((l) => l.artefact_type === 'framework');
  const linkedControls = links.filter((l) => l.artefact_type === 'control');
  // Default-view search/filter over the links actually present (evidence can link to any of the
  // 19 artefact types, not just Framework/Control). Manage mode has its own, different tooling.
  const linkTypes = Array.from(new Set(links.map((l) => l.artefact_type as string)));
  // artefactTypeFilter is free-standing state that can outlive the type it names (e.g. the last
  // link of that type gets unlinked via Manage) - fall back to "All types" rather than filtering
  // on a type no longer present, which would silently show "No linked artefacts match" while the
  // <select> itself displays "All types" (no matching <option> left to select).
  const effectiveTypeFilter = linkTypes.includes(artefactTypeFilter) ? artefactTypeFilter : '';
  const visibleLinks = links.filter((l) => {
    if (effectiveTypeFilter && l.artefact_type !== effectiveTypeFilter) return false;
    const q = artefactQuery.trim().toLowerCase();
    if (!q) return true;
    const t = l.artefact_title && typeof l.artefact_title === 'object' ? (l.artefact_title as any).value : l.artefact_title;
    return `${l.artefact_display_id || ''} ${t || ''} ${l.artefact_type}`.toLowerCase().includes(q);
  });

  const accountablePerson = useMemo(() => {
    const a = (evidenceRaci as any[]).find((r) => r.role === 'A');
    if (!a) return null;
    return (people as Person[]).find((p) => p.personId === a.personId) || null;
  }, [evidenceRaci, people]);

  // Populate fieldValues from the loaded item, same sanitization/backfill RegisterItemDetail uses.
  useEffect(() => {
    if (!evidenceItem || typeof evidenceItem !== 'object') return;
    setFieldValues(sanitizeEvidenceItem(evidenceItem));
  }, [evidenceItem]);

  const groupedFields = useMemo(() => {
    const effective = (fieldConfigs as FieldConfig[]).length === 0 ? DEFAULT_EVIDENCE_FIELDS : (fieldConfigs as FieldConfig[]);
    return effective.reduce((acc: Record<number, FieldConfig[]>, c) => {
      const n = Number(c.cardNumber || 1);
      if (!acc[n]) acc[n] = [];
      acc[n].push(c);
      return acc;
    }, {});
  }, [fieldConfigs]);

  const effectiveFieldConfigsByCard = useMemo(() => {
    const result: Record<number, FieldConfig[]> = {};
    Object.entries(groupedFields).forEach(([numStr, fields]) => {
      const visible = fields.filter((c) => c.isVisible).sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
      result[Number(numStr)] = visible.map((c) => ({ ...c, isEditable: isEditing ? (c.isSystemField ? false : c.isEditable) : false }));
    });
    return result;
  }, [groupedFields, isEditing]);

  // Which card holds which bespoke section - detected by field CONTENT (matching
  // RegisterItemDetail.tsx's own sniffing), not a hardcoded card number, so a register whose
  // live FieldConfig assigns these fields to different card numbers than the default still
  // renders (and validates) correctly rather than silently losing whole cards.
  const cardEntries = Object.entries(effectiveFieldConfigsByCard);
  const identityCardNum = cardEntries.find(([, f]) => f.some((c) => c.fieldId === 'evidence_code'))?.[0];
  const scheduleCardNum = cardEntries.find(([, f]) => f.some((c) => c.fieldId === 'schedule_status'))?.[0];
  const reviewNotesCardNum = cardEntries.find(([, f]) => f.some((c) => c.fieldId === 'improvement_needed'))?.[0];
  const metadataCardNum = cardEntries.find(([, f]) => f.some((c) => c.fieldId === 'source_tab'))?.[0];
  const bespokeCardNums = new Set([identityCardNum, scheduleCardNum, reviewNotesCardNum, metadataCardNum].filter(Boolean));
  // Any other live card (a register-specific custom card beyond the standard four) still
  // renders through the same generic FieldRenderer grid the old page used for it.
  const genericCards = cardEntries.filter(([num]) => !bespokeCardNums.has(num));

  const metadataCardFields = metadataCardNum ? effectiveFieldConfigsByCard[Number(metadataCardNum)] : null;

  // Every field actually rendered somewhere on this page is validated - Identity, every generic
  // card, and Metadata's own fields; Schedule/Review Notes are excluded because their bespoke
  // components have no inline-error UI to show a message against (same exclusion
  // RegisterItemDetail.tsx makes, for the same reason).
  const validableFieldConfigs = useMemo(() => {
    const identity = identityCardNum ? effectiveFieldConfigsByCard[Number(identityCardNum)] : [];
    const generic = genericCards.flatMap(([, f]) => f);
    return [...identity, ...generic, ...(metadataCardFields || [])];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveFieldConfigsByCard, identityCardNum, metadataCardNum]);

  const handleFieldChange = (fieldId: string, value: any) => {
    setFieldValues((prev) => {
      const updated = { ...prev, [fieldId]: value };
      const lastReviewed = fieldId === 'evidence_last_reviewed' ? value : prev['evidence_last_reviewed'];
      if (fieldId === 'evidence_last_reviewed' || fieldId === 'collection_frequency') {
        const freq = fieldId === 'collection_frequency' ? value : prev['collection_frequency'];
        const next = calcDateFromFrequency(lastReviewed as string, freq as string);
        if (next) updated['next_due_date'] = next;
      }
      return updated;
    });
    setInvalidFieldIds((prev) => {
      if (!prev.has(fieldId)) return prev;
      const next = new Set(prev);
      next.delete(fieldId);
      return next;
    });
  };

  const saveMutation = useMutation({
    mutationFn: (payload: any) => (isNew ? evidenceApi.create(payload) : evidenceApi.update(itemId!, payload)),
    onSuccess: (response: any) => {
      if (isNew) {
        const newId = response?.evidence?.evidenceId ?? response?.evidenceId;
        if (newId) {
          toast.success('Evidence created.');
          window.location.href = `/evidence/${newId}?registerId=${encodeURIComponent(registerId)}`;
          return;
        }
      }
      queryClient.invalidateQueries({ queryKey: ['evidence', itemId] });
      queryClient.invalidateQueries({ queryKey: ['list'] });
      setIsEditing(false);
      toast.success('Evidence updated.');
    },
    onError: (e: Error) => toast.error(`Failed to save: ${e.message}`),
  });

  // Content-only edit, independent of the Details section's own edit mode/Save button - the
  // shared Evidence Requirements list needs to be addable/editable on its own, the same
  // immediacy as Team Contributions/RACI/Linked Artefacts (each of those "save immediately on
  // their own actions" per the hint text at the bottom of this page).
  const requirementsMutation = useMutation({
    mutationFn: (deliverables: Deliverable[]) => evidenceApi.update(itemId!, { deliverables }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['evidence', itemId] }),
    onError: (e: Error) => toast.error(`Failed to save requirements: ${e.message}`),
  });

  const handleSave = () => {
    if (!registerId) {
      toast.warning('Missing registerId in URL. Open this page with ?registerId=...');
      return;
    }
    const missing = getMissingRequiredFields(validableFieldConfigs, fieldValues);
    if (missing.length > 0) {
      setInvalidFieldIds(new Set(missing));
      toast.error('Please fill in the required fields.');
      return;
    }
    setInvalidFieldIds(new Set());
    const dataToSave: Record<string, any> = { ...fieldValues };
    (fieldConfigs as FieldConfig[]).forEach((c) => { if (c.isCalculated || !c.isEditable) delete dataToSave[c.fieldId]; });
    saveMutation.mutate({ registerId, tenantId: getTenantId(), fields: dataToSave });
  };

  // ── Team Contributions derivation ──
  const teamNames = useMemo(() => {
    const names: string[] = [];
    submissions.forEach((s) => { const t = s.teamName || UNNAMED_TEAM; if (!names.includes(t)) names.push(t); });
    pendingTeams.forEach((t) => { if (!names.includes(t.name)) names.push(t.name); });
    return names;
  }, [submissions, pendingTeams]);

  const teamData = useMemo(() => {
    return teamNames.map((team) => {
      const teamSubs = submissions.filter((s) => (s.teamName || UNNAMED_TEAM) === team);
      const [latest, ...earlier] = teamSubs;
      const pendingDepartmentId = pendingTeams.find((t) => t.name === team)?.departmentId;
      return { team, latest, earlier, pendingDepartmentId };
    });
  }, [teamNames, submissions, pendingTeams]);

  const acceptedCount = teamData.filter((t) => t.latest?.status === 'Accepted').length;
  const awaitingCount = teamData.filter((t) => t.latest && (t.latest.status === 'Submitted' || t.latest.status === 'In Review' || t.latest.status === 'Clarification Needed')).length;
  // The card-head chips split the merged "awaiting" bucket the Health tile, the review-queue
  // button and computeHealth() all use: undecided rounds vs rounds sent back for clarification
  // (the two always sum to awaitingCount).
  const awaitingDecisionCount = teamData.filter((t) => t.latest && (t.latest.status === 'Submitted' || t.latest.status === 'In Review')).length;
  const clarificationCount = teamData.filter((t) => t.latest?.status === 'Clarification Needed').length;
  const notSubmittedCount = teamData.filter((t) => !t.latest).length;

  const evidenceDeliverables: Deliverable[] = (evidenceItem?.deliverables as Deliverable[]) || [];
  // "of" is the count of teams this requirement currently applies to (every team minus
  // excludedTeams) - not teamData.length unconditionally, so an excluded team is never counted
  // against a requirement's completion.
  const requirementRollup = evidenceDeliverables.map((req) => {
    const applicable = teamData.filter((t) => appliesToTeam(req, t.team));
    const doneCount = applicable.filter((t) => t.latest?.deliverables?.some((d) => d.id === req.id && d.status === 'Done')).length;
    return { ...req, doneCount, of: applicable.length };
  });
  // Per-team "N/M requirements done" for the matrix header - a SEPARATE derived value from
  // TeamRow's own progress ring, which counts everything in that team's stored round including
  // ad-hoc items with no master-list counterpart. These two numbers can legitimately diverge for
  // the same team; that's expected, not a bug to reconcile.
  const teamRollup = new Map(teamData.map(({ team, latest }) => {
    const applicable = evidenceDeliverables.filter((r) => appliesToTeam(r, team));
    const done = applicable.filter((r) => latest?.deliverables?.find((d) => d.id === r.id)?.status === 'Done').length;
    return [team, { done, of: applicable.length }];
  }));
  const cellsDone = requirementRollup.reduce((n, r) => n + r.doneCount, 0);
  const cellsOf = requirementRollup.reduce((n, r) => n + r.of, 0);

  const health = computeHealth(
    fieldValues['evidence_status'] as string,
    fieldValues['schedule_status'] as string,
    fieldValues['next_due_date'] as string,
    teamData.map((t) => ({ team: t.team, sub: t.latest })),
  );

  // ── Control Effectiveness legend context (static text, but linked controls are real) ──

  const jumpToReviewQueue = () => {
    const firstAwaiting = teamData.find((t) => t.latest && (t.latest.status === 'Submitted' || t.latest.status === 'In Review' || t.latest.status === 'Clarification Needed'));
    if (firstAwaiting) {
      openTeam(firstAwaiting.team);
      setHighlightedTeam(firstAwaiting.team);
      setTimeout(() => setHighlightedTeam(null), 2800);
    }
    goToOverview('team');
  };

  // Same jump-and-pulse mechanism as jumpToReviewQueue, for a specific team named by the matrix
  // cell popover's "View team's round" link, rather than the first-awaiting lookup.
  const jumpToTeam = (team: string) => {
    openTeam(team);
    setHighlightedTeam(team);
    setTimeout(() => setHighlightedTeam(null), 2800);
    goToOverview('team');
  };

  // Requirements Matrix write path: every edit (assignment toggle, description/due-date, add,
  // remove) goes through this one mutation - the same call the old standalone Manage editor used
  // - so there is exactly one write path onto evidenceDeliverables. Reads/writes off the page's
  // own evidenceDeliverables snapshot, not the matrix's own state, since the matrix is
  // presentational only.
  const toggleRequirementAssignment = (reqId: string, team: string) => {
    // Exclusion list, so toggling one team never touches any other team's coverage - no
    // "materialise into an explicit list" step, and excluding every current team is still a
    // well-formed, unambiguous state (distinct from "applies to all"), unlike an inclusion list's
    // empty array, which collides with its own default.
    const next = evidenceDeliverables.map((d) => {
      if (d.id !== reqId) return d;
      const excluded = d.excludedTeams || [];
      const currentlyApplies = !excluded.includes(team);
      return { ...d, excludedTeams: currentlyApplies ? [...excluded, team] : excluded.filter((t) => t !== team) };
    });
    requirementsMutation.mutate(next);
  };
  const editRequirement = (reqId: string, patch: Partial<Deliverable>) => {
    requirementsMutation.mutate(evidenceDeliverables.map((d) => (d.id === reqId ? { ...d, ...patch } : d)));
  };
  const addRequirement = () => {
    requirementsMutation.mutate([...evidenceDeliverables, { id: `DLV-${Date.now()}`, description: '', status: 'Pending', dueDate: '' }]);
  };
  const removeRequirement = (reqId: string) => {
    requirementsMutation.mutate(evidenceDeliverables.filter((d) => d.id !== reqId));
  };

  if (!isNew && itemLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
        </div>
      </div>
    );
  }

  // New, unsaved evidence has no submissions/RACI/attachments/links to show yet - a minimal
  // create form (just the Details fields, always in edit mode) rather than the full record.
  if (isNew) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <div className="np-page-head">
          <span className="np-icon-badge np-tone-neutral"><i className="ri-shield-check-line"></i></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="np-crumb">Evidence Registers &rsaquo; Evidence <span className="np-crumb-current">&rsaquo; New Evidence</span></div>
            <h1>New Evidence</h1>
          </div>
          <div className="np-page-actions">
            <button className="np-btn np-btn-ghost" onClick={() => window.history.back()}><i className="ri-arrow-left-line"></i>Back</button>
          </div>
        </div>
        <IdentityCard
          cardFields={identityCardNum ? effectiveFieldConfigsByCard[Number(identityCardNum)] : []}
          fieldValues={fieldValues}
          isEditing={true}
          onChange={handleFieldChange}
          people={people as Person[]}
          assets={assets as Asset[]}
          departments={departments as Department[]}
          invalidFieldIds={invalidFieldIds}
        />
        <CollectionScheduleCard fieldValues={fieldValues} onChange={handleFieldChange} isEditing={true} evidenceId="" submissions={[]} />
        <ReviewNotesCard cardFields={reviewNotesCardNum ? effectiveFieldConfigsByCard[Number(reviewNotesCardNum)] : []} fieldValues={fieldValues} isEditing={true} onChange={handleFieldChange} />
        <div className="np-banner np-tone-info"><i className="ri-information-line"></i>Save this evidence record first to unlock Team Contributions, Attachments, RACI, Linked Artefacts, and Control Effectiveness.</div>
        <div className="np-action-bar">
          <button className="np-btn np-btn-ghost" onClick={() => window.history.back()}>Cancel</button>
          <button className="np-btn np-btn-primary" disabled={saveMutation.isPending} onClick={handleSave}>
            {saveMutation.isPending ? <span className="spinner-border spinner-border-sm"></span> : 'Create'}
          </button>
        </div>
      </div>
    );
  }

  const title = (fieldValues['evidence_title'] as string) || evidenceItem?.evidenceId || 'Evidence';
  const statusVal = String(fieldValues['evidence_status'] || '');
  const statusTone = /active|approved|accepted/i.test(statusVal) ? 'success' : 'neutral';
  const accountableName = accountablePerson
    ? ([(accountablePerson as any).firstName, (accountablePerson as any).lastName].filter(Boolean).join(' ').trim() || accountablePerson.personName)
    : '';

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">

      {/* ── Header ── */}
      <div className="np-page-head">
        <span className={`np-icon-badge np-tone-${health.tone === 'neutral' ? 'neutral' : health.tone}`}>
          <i className="ri-shield-check-line"></i>
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="np-crumb">Evidence Registers &rsaquo; Evidence <span className="np-crumb-current">&rsaquo; {title}</span></div>
          <h1>{title}</h1>
        </div>
        <div className="np-page-actions">
          <button className="np-btn np-btn-ghost" onClick={() => window.history.back()}><i className="ri-arrow-left-line"></i>Back</button>
          {!isEditing && (
            <button className="np-btn np-btn-ghost" onClick={() => { setIsEditing(true); goToOverview('details'); }}><i className="ri-pencil-line"></i>Edit</button>
          )}
          {isEditing && (
            <>
              {/* Same Cancel body as the old floating action bar - discard unsaved Details edits by
                  rebuilding fieldValues from the server item, since the populate effect alone never
                  re-runs on Cancel (evidenceItem itself hasn't changed). */}
              <button
                className="np-btn np-btn-ghost"
                disabled={saveMutation.isPending}
                onClick={() => {
                  setIsEditing(false);
                  setInvalidFieldIds(new Set());
                  if (evidenceItem && typeof evidenceItem === 'object') setFieldValues(sanitizeEvidenceItem(evidenceItem));
                }}
              >
                Cancel
              </button>
              <button className="np-btn np-btn-primary" disabled={saveMutation.isPending} onClick={handleSave}>
                {saveMutation.isPending ? <span className="spinner-border spinner-border-sm"></span> : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>
      {isEditing && (
        <p className="np-hint mb-3">Saves Details only — Team Contributions, RACI, Attachments and Linked Artefacts save immediately on their own actions.</p>
      )}
      <div className="d-flex flex-wrap gap-2 mb-4 np-head-chip-row">
        {statusVal && <span className={`np-chip np-tone-${statusTone}`}>{statusVal}</span>}
        {fieldValues['type'] ? <span className="np-chip np-tone-neutral">{String(fieldValues['type'])}</span> : null}
        {/* `title` already falls back to the id when evidence_title is empty - never show it twice. */}
        {evidenceItem?.evidenceId && title !== evidenceItem.evidenceId && (
          <span className="np-chip np-tone-neutral np-tabular">{evidenceItem.evidenceId}</span>
        )}
        {linkedFrameworks.map((fw) => (
          <span key={fw.artefact_id} className="np-chip np-tone-primary">{fw.artefact_title}</span>
        ))}
        <span className="np-chip np-tone-primary">{links.length} linked artefacts</span>
        {accountablePerson && (
          <span className="np-chip np-tone-neutral">
            <span className="avatar avatar-xs bg-label-primary rounded-circle me-1" style={{ width: '1.1rem', height: '1.1rem', fontSize: '0.55rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{initials(accountableName)}</span>
            {accountableName}
            {' · Accountable'}
          </span>
        )}
      </div>

      {/* ── Health & Rollup ── */}
      <div className={`np-card np-card-accent-${health.tone}`}>
        <div className="np-card-body">
          <div className="row g-3 align-items-center">
            <div className="col-12 col-md-8">
              <div className={`np-health-headline np-tone-${health.tone}`}>
                <i className={`ri-${health.tone === 'success' ? 'checkbox-circle' : health.tone === 'danger' ? 'error-warning' : health.tone === 'warning' ? 'alert' : 'information'}-line`}></i>
                <span>{health.label}</span>
              </div>
              <p className="np-sub mb-2" style={{ marginLeft: '2rem', maxWidth: '38rem' }}>{health.detail}</p>
              {linkedControls.length > 0 && (
                <div className="d-flex align-items-center gap-2 flex-wrap np-sub mb-2" style={{ marginLeft: '2rem', fontSize: 'var(--np-text-sm)' }}>
                  Feeds Control Effectiveness for:
                  {linkedControls.map((c) => (
                    <a key={c.artefact_id} href={`/controls/${c.artefact_id}`} className="np-chip np-tone-info np-tabular">{c.artefact_display_id || c.artefact_id}</a>
                  ))}
                </div>
              )}
              {awaitingCount > 0 && (
                <button className="np-btn np-btn-warning np-btn-sm" style={{ marginLeft: '2rem' }} onClick={jumpToReviewQueue}>
                  {awaitingCount} awaiting your review <i className="ri-arrow-right-s-line"></i>
                </button>
              )}
            </div>
            <div className="col-12 col-md-4">
              <div className="np-mini-stat-grid np-mini-stat-grid-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="np-mini-stat np-mini-stat-link" onClick={() => goToOverview('team')}>
                  <div className="np-mini-stat-value">{teamData.length}</div>
                  <div className="np-mini-stat-label">Teams</div>
                </div>
                <div className="np-mini-stat np-mini-stat-link" onClick={() => goToOverview('team')}>
                  <div className={`np-mini-stat-value${acceptedCount > 0 ? ' np-tone-success' : ''}`}>{acceptedCount}</div>
                  <div className="np-mini-stat-label">Accepted</div>
                </div>
                <div className="np-mini-stat np-mini-stat-link" onClick={jumpToReviewQueue}>
                  <div className={`np-mini-stat-value${awaitingCount > 0 ? ' np-tone-warning' : ''}`}>{awaitingCount}</div>
                  <div className="np-mini-stat-label">Awaiting review</div>
                </div>
                <div className="np-mini-stat np-mini-stat-link" onClick={() => goToOverview('team')}>
                  <div className={`np-mini-stat-value${notSubmittedCount > 0 ? ' np-tone-danger' : ''}`}>{notSubmittedCount}</div>
                  <div className="np-mini-stat-label">Not submitted</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Exclusive tabs — the app's real .np-tabs/.np-tab primitive, the same
          activeTab === 'x' pattern ControlDetail.tsx/RiskDetail.tsx/RegisterItemDetail.tsx already
          use for these same four sections. Only one panel is mounted at a time - no sticky rail
          (a "scroll to section" rail has no meaning once sections aren't all on screen). ── */}
      <div className="np-tabs" role="tablist">
        <button type="button" className={`np-tab d-flex align-items-center gap-1${activeTab === 'overview' ? ' active' : ''}`} onClick={() => setActiveTab('overview')}>
          <i className="ri-file-list-3-line"></i>Overview
        </button>
        <button type="button" className={`np-tab d-flex align-items-center gap-1${activeTab === 'raci' ? ' active' : ''}`} onClick={() => setActiveTab('raci')}>
          <i className="ri-team-line"></i>RACI
          {!!(evidenceRaci as any[]).length && <span className="np-chip np-tone-neutral np-tabular">{(evidenceRaci as any[]).length + teamRoundRaciCount}</span>}
        </button>
        <button type="button" className={`np-tab d-flex align-items-center gap-1${activeTab === 'attachments' ? ' active' : ''}`} onClick={() => setActiveTab('attachments')}>
          <i className="ri-attachment-2"></i>Attachments
          {!!evidenceDocs.length && <span className="np-chip np-tone-neutral np-tabular">{evidenceDocs.length}</span>}
        </button>
        <button type="button" className={`np-tab d-flex align-items-center gap-1${activeTab === 'artefacts' ? ' active' : ''}`} onClick={() => setActiveTab('artefacts')}>
          <i className="ri-links-line"></i>Linked Artefacts
          {!!links.length && <span className="np-chip np-tone-neutral np-tabular">{links.length}</span>}
        </button>
      </div>

      {activeTab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* ══ Control Effectiveness Transparency ══ */}
          <section id="effectiveness" ref={overviewRefs.effectiveness as any} className="np-card np-record-section">
            <div className="np-card-head"><h2>Control Effectiveness Transparency</h2></div>
            <div className="np-card-body">
              {linkedControls.length === 0 ? (
                <div className="text-center np-sub py-3">
                  <i className="ri-shield-line d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
                  Not linked to any Control yet —{' '}
                  <button type="button" className="np-link-action" onClick={() => setActiveTab('artefacts')}>link one via the Linked Artefacts tab</button>{' '}
                  to see how this evidence affects its score.
                </div>
              ) : (
                <>
                  <div className="d-flex align-items-center gap-2 flex-wrap mb-3 np-sub">
                    This evidence feeds:
                    {linkedControls.map((c) => (
                      <a key={c.artefact_id} href={`/controls/${c.artefact_id}`} className="np-chip np-tone-info np-tabular">{c.artefact_display_id || c.artefact_id}</a>
                    ))}
                  </div>
                  <div className="np-row2">
                    <div>
                      {/* Heading in the accessible text-emphasis ink (what every other np-* toned value
                          uses), not the bright base green the check icons are drawn in. */}
                      <strong className="d-block mb-2 text-success-emphasis" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Moves the score</strong>
                      {['Linked-evidence presence & count', 'Accepted-submission fraction', 'Most recent review / accept date'].map((t) => (
                        <div key={t} className="d-flex align-items-start gap-2 py-1"><i className="ri-checkbox-circle-line text-success mt-1"></i><span>{t}</span></div>
                      ))}
                    </div>
                    <div>
                      <strong className="d-block mb-2 np-sub" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Captured here, not scored</strong>
                      {['Strength / Assurance override', 'Review notes & comments', 'Deliverables (either level) · Timeliness · RACI'].map((t) => (
                        <div key={t} className="py-1 np-sub">{t}</div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* ══ Details ══ */}
          {/* One unified card: Identity / Collection Schedule / Review Notes (and any register-
              specific extra card, plus Metadata) render as .np-details-group sub-groups via the
              shared cards' additive `embedded` prop - the create form below keeps the standalone cards. */}
          <section id="details" ref={overviewRefs.details as any} className="np-card np-record-section">
            <div className="np-card-head"><h2>Details</h2></div>
            <div className="np-card-body">
              <IdentityCard
                embedded
                cardFields={identityCardNum ? effectiveFieldConfigsByCard[Number(identityCardNum)] : []}
                fieldValues={fieldValues}
                isEditing={isEditing}
                onChange={handleFieldChange}
                people={people as Person[]}
                assets={assets as Asset[]}
                departments={departments as Department[]}
                invalidFieldIds={invalidFieldIds}
                // Same statusTone as the header chip, so the two Status chips can never disagree.
                renderValue={(fieldId, value) => (fieldId === 'evidence_status' && value ? <span className={`np-chip np-tone-${statusTone}`}>{value}</span> : undefined)}
              />
              <CollectionScheduleCard
                embedded
                fieldValues={fieldValues}
                onChange={handleFieldChange}
                isEditing={isEditing}
                evidenceId={itemId!}
                submissions={submissions as any}
              />
              <ReviewNotesCard
                embedded
                cardFields={reviewNotesCardNum ? effectiveFieldConfigsByCard[Number(reviewNotesCardNum)] : []}
                fieldValues={fieldValues}
                isEditing={isEditing}
                onChange={handleFieldChange}
              />

              {/* Any register-specific custom card beyond the standard four still renders here,
                  through the same generic FieldRenderer list the old page used - a live register
                  with an extra card no longer silently loses it. */}
              {genericCards.map(([cardNum, fields]) => (
                <div key={cardNum} className="np-details-group">
                  <span className="eyebrow-label">{fields[0]?.cardName || `Card ${cardNum}`}</span>
                  {fields.map((config) => (
                    <FieldRenderer
                      key={config.fieldId}
                      config={config}
                      value={fieldValues[config.fieldId]}
                      onChange={handleFieldChange}
                      people={people as Person[]}
                      assets={assets as Asset[]}
                      departments={departments as Department[]}
                      error={invalidFieldIds.has(config.fieldId) ? 'This field is required.' : undefined}
                    />
                  ))}
                </div>
              ))}

              {/* Metadata: a real editable sub-group while editing, a minimal ruled footer strip in
                  view mode - same treatment RegisterItemDetail.tsx gave it, so 'Imported from Y' (a
                  register that defines it) and correcting Uploaded By/Date Uploaded still work. */}
              {metadataCardFields && isEditing && (
                <div className="np-details-group">
                  <span className="eyebrow-label">Metadata</span>
                  {metadataCardFields.map((config) => (
                    <FieldRenderer
                      key={config.fieldId}
                      config={config}
                      value={fieldValues[config.fieldId]}
                      onChange={handleFieldChange}
                      people={people as Person[]}
                      assets={assets as Asset[]}
                      departments={departments as Department[]}
                      error={invalidFieldIds.has(config.fieldId) ? 'This field is required.' : undefined}
                    />
                  ))}
                </div>
              )}
              {metadataCardFields && !isEditing && (
                <div className="np-details-footer">
                  {metadataCardFields.map((config) => {
                    const value = String(fieldValues[config.fieldId] ?? '').trim();
                    if (!value) return null;
                    const icon = config.fieldId === 'evidence_uploaded_by' ? 'ri-user-line' : config.fieldId === 'evidence_date_uploaded' ? 'ri-calendar-line' : 'ri-file-copy-2-line';
                    const text =
                      config.fieldId === 'evidence_uploaded_by' ? `Uploaded by ${value}`
                      : config.fieldId === 'evidence_date_uploaded' ? (() => { const d = new Date(value); return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); })()
                      : `Imported from ${value}`;
                    return <span key={config.fieldId} className="d-flex align-items-center gap-1"><i className={icon}></i>{text}</span>;
                  })}
                </div>
              )}
            </div>
          </section>

          {/* ══ Team Contributions ══ */}
          <section id="team" ref={overviewRefs.team as any} className="np-card np-record-section">
            <div className="np-card-head">
              <h2>Team Contributions</h2>
              <span className="np-chip np-tone-neutral">{teamData.length} team{teamData.length === 1 ? '' : 's'}</span>
              {acceptedCount > 0 && <span className="np-chip np-tone-success">{acceptedCount} Accepted</span>}
              {awaitingDecisionCount > 0 && <span className="np-chip np-tone-warning">{awaitingDecisionCount} Awaiting</span>}
              {clarificationCount > 0 && <span className="np-chip np-tone-warning">{clarificationCount} Clarification Needed</span>}
              {notSubmittedCount > 0 && <span className="np-chip np-tone-danger">{notSubmittedCount} Not submitted</span>}
              <div className="d-flex align-items-center gap-2 ms-auto">
                <div className="np-pill-toggle-group" role="group" aria-label="View">
                  <button type="button" className={`np-pill-toggle np-tone-primary${teamContribView === 'owner' ? ' active' : ''}`} onClick={() => setTeamContribView('owner')}>Compliance Owner</button>
                  <button type="button" className={`np-pill-toggle np-tone-primary${teamContribView === 'team' ? ' active' : ''}`} onClick={() => setTeamContribView('team')}>As a Team</button>
                </div>
                {/* Add Team / Edit Assignments are structural edits - hidden until the page's own
                    Edit is on, same as every other Manage/Upload/Sync action on this page now. */}
                {isEditing && teamContribView === 'owner' && (
                  <button
                    type="button"
                    className="np-btn np-btn-ghost np-btn-sm"
                    onClick={() => setEditingAssignments((v) => !v)}
                  >
                    <i className="ri-settings-3-line"></i>{editingAssignments ? 'Done' : 'Edit Assignments'}
                  </button>
                )}
                {isEditing && (
                  <button className="np-btn np-btn-primary np-btn-sm" onClick={() => setAddTeamOpen(true)}>
                    <i className="ri-add-line"></i>Add Team
                  </button>
                )}
              </div>
            </div>
            <div className="np-card-body">
              {teamContribView === 'owner' ? (
                teamData.length === 0 ? (
                  <div className="text-center np-sub py-4">
                    <i className="ri-group-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
                    No teams yet. Add one to start collecting evidence for this requirement.
                  </div>
                ) : (
                <>
                  <div className="np-mini-stat-grid mb-4">
                    <div className="np-mini-stat">
                      <div className="np-mini-stat-value">{teamData.length}</div>
                      <div className="np-mini-stat-label">Teams</div>
                    </div>
                    <div className="np-mini-stat">
                      <div className="np-mini-stat-value">{evidenceDeliverables.length}</div>
                      <div className="np-mini-stat-label">Requirements</div>
                    </div>
                    <div className="np-mini-stat">
                      <div className={`np-mini-stat-value${cellsOf > 0 && cellsDone === cellsOf ? ' np-tone-success' : ''}`}>{cellsDone}/{cellsOf}</div>
                      <div className="np-mini-stat-label">Cells done</div>
                    </div>
                  </div>

                  <RequirementsMatrix
                    requirements={evidenceDeliverables}
                    teamData={teamData}
                    editing={editingAssignments && isEditing}
                    saving={requirementsMutation.isPending}
                    onToggleAssignment={toggleRequirementAssignment}
                    onEditRequirement={editRequirement}
                    onAddRequirement={addRequirement}
                    onRemoveRequirement={removeRequirement}
                    onJumpToTeam={jumpToTeam}
                  />

                  <div className="workflow-section mt-4">
                    <div className="d-flex align-items-baseline gap-2 mb-3 flex-wrap">
                      <span className="eyebrow-label mb-0">Team Workflow</span>
                      <span className="np-hint">Round history, decisions, RACI, files &amp; comments per team</span>
                    </div>
                    <div className="np-team-accordion">
                      {teamData.map(({ team, latest, earlier, pendingDepartmentId }) => (
                        <TeamRow
                          key={team}
                          evidenceId={itemId!}
                          teamName={team}
                          pendingDepartmentId={pendingDepartmentId}
                          latest={latest}
                          earlier={earlier}
                          isAdmin={isAdmin}
                          expanded={expandedTeams.has(team)}
                          highlighted={highlightedTeam === team}
                          people={people as Person[]}
                          nextDueDate={fieldValues['next_due_date'] as string | undefined}
                          requirementCount={teamRollup.get(team)?.of ?? 0}
                          onToggle={() => toggleTeam(team)}
                          onViewFullRaci={() => setActiveTab('raci')}
                        />
                      ))}
                    </div>
                  </div>
                </>
                )
              ) : (
                <TeamSubmissionPanel
                  evidenceId={itemId!}
                  teamData={teamData.map((t) => ({
                    team: t.team,
                    teamDepartmentId: t.latest?.teamDepartmentId || t.pendingDepartmentId,
                    latest: t.latest,
                  }))}
                  requirements={evidenceDeliverables}
                  gracePeriodDays={Number(fieldValues['grace_period_days']) || 0}
                  dueDate={fieldValues['next_due_date'] as string | undefined}
                  people={people as Person[]}
                  pickedTeam={pickedTeam && teamData.some((t) => t.team === pickedTeam) ? pickedTeam : teamData[0]?.team ?? null}
                  onPickTeam={setPickedTeam}
                />
              )}
            </div>
          </section>
        </div>
      )}

      {/* ══ RACI (exclusive tab) ══ */}
      {activeTab === 'raci' && (
        <section className="np-card">
          <div className="np-card-head">
            {/* Grand total - evidence-level plus every team round's assignments (the tab badge
                shows the same number); the hint below explains why it exceeds the visible rows. */}
            <h2>RACI ({(evidenceRaci as any[]).length + teamRoundRaciCount})</h2>
            {/* The wrapper is required: .np-card-head .np-btn { margin-left: auto } would otherwise
                give both buttons an auto margin and spread them across the head. */}
            <div className="d-flex align-items-center gap-2 ms-auto">
              {isEditing ? (
                <>
                  <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={syncRaciFromControls} disabled={raciSyncing} title="Copy RACI from all linked controls (union, no duplicates)">
                    {raciSyncing ? <span className="spinner-border spinner-border-sm" role="status"></span> : <i className="ri-loop-right-line"></i>}Sync from Controls
                  </button>
                  <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => setRaciManageOpen((v) => !v)}>
                    <i className="ri-settings-3-line"></i>{raciManageOpen ? 'Done' : 'Manage'}
                  </button>
                </>
              ) : (
                <span className="np-hint"><i className="ri-lock-line me-1"></i>Click Edit to manage</span>
              )}
            </div>
          </div>
          <div className="np-card-body">
            {teamRoundRaciCount > 0 && (
              <div className="np-hint mb-2">
                +{teamRoundRaciCount} additional assignment{teamRoundRaciCount === 1 ? '' : 's'} recorded against individual team rounds —{' '}
                <button type="button" className="np-link-action" onClick={() => goToOverview('team')}>view all ↓</button>
              </div>
            )}
            {raciManageOpen && isEditing ? (
              <RaciAssignments artefactId={itemId!} artefactType="evidence" />
            ) : (evidenceRaci as any[]).length === 0 ? (
              <div className="text-center np-sub py-3">
                <i className="ri-team-line d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
                No RACI assignments yet.
              </div>
            ) : (
              <div className="np-table-wrap">
                <table className="np-table">
                  <thead><tr><th>Person</th><th>Role</th><th>Notes</th></tr></thead>
                  <tbody>
                    {(evidenceRaci as any[]).map((r) => {
                      const personName = (people as Person[]).find((p) => p.personId === r.personId)?.personName || r.personId;
                      const roleTone = { R: 'primary', A: 'success', C: 'info', I: 'warning' }[r.role as string] || 'neutral';
                      return (
                        <tr key={r.raciId}>
                          <td>
                            <span className={`avatar avatar-xs ${r.role === 'A' ? 'bg-label-success' : 'bg-label-secondary'} rounded-circle me-2`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span className="fw-bold" style={{ fontSize: '0.65rem' }}>{initials(personName)}</span>
                            </span>
                            {personName}
                          </td>
                          <td><span className={`np-chip np-tone-${roleTone}`}>{r.role}</span></td>
                          <td className="np-hint">{r.notes || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══ Attachments (exclusive tab) ══ */}
      {activeTab === 'attachments' && (
        <section className="np-card">
          <div className="np-card-head"><h2>Attachments ({evidenceDocs.length})</h2></div>
          <div className="np-card-body">
            {/* embedded: DocumentManager would otherwise nest its own full card in this one;
                bare: this card head already titles it, so no second "Attachments (N)" eyebrow and
                no border-top divider under the head. readOnly hides Upload/Delete (keeps
                Download) until Edit is on - the exact split DocumentManager already supports, no
                changes needed to that component. */}
            <DocumentManager entityType="evidence" entityId={itemId!} embedded bare readOnly={!isEditing} />
          </div>
        </section>
      )}

      {/* ══ Linked Artefacts (exclusive tab) ══ */}
      {activeTab === 'artefacts' && (
        <section className="np-card">
          <div className="np-card-head">
            <h2>Linked Artefacts ({links.length})</h2>
            {isEditing ? (
              <button type="button" className="np-btn np-btn-ghost np-btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setArtefactsManageOpen((v) => !v)}>
                <i className="ri-settings-3-line"></i>{artefactsManageOpen ? 'Done' : 'Manage'}
              </button>
            ) : (
              <span className="np-hint" style={{ marginLeft: 'auto' }}><i className="ri-lock-line me-1"></i>Click Edit to manage</span>
            )}
          </div>
          <div className="np-card-body">
            {artefactsManageOpen && isEditing ? (
              <LinkedArtefacts artefactId={itemId!} artefactType="evidence" artefactRegister="evidence_register" registerId={registerId} />
            ) : links.length === 0 ? (
              <div className="text-center np-sub py-3">
                <i className="ri-links-line d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
                No linked artefacts yet.
              </div>
            ) : (
              <>
                {/* .np-search has min-width 15rem, so flex-wrap is what keeps this row inside a
                    phone viewport; .np-select is width:100% so the inline width:auto is required. */}
                <div className="d-flex gap-2 flex-wrap mb-3">
                  <div className="np-search" style={{ flex: 1, minWidth: 0 }}>
                    <i className="ri-search-line"></i>
                    <input type="text" placeholder="Search or filter linked artefacts…" value={artefactQuery} onChange={(e) => setArtefactQuery(e.target.value)} />
                  </div>
                  <select className="np-select" style={{ flex: '0 0 auto', width: 'auto', maxWidth: '9rem' }} value={effectiveTypeFilter} onChange={(e) => setArtefactTypeFilter(e.target.value)}>
                    <option value="">All types</option>
                    {linkTypes.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                {visibleLinks.length === 0 ? (
                  <div className="np-sub small py-2">No linked artefacts match.</div>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {visibleLinks.map((l) => {
                      // This page's links query has no sanitizing `select` (unlike LinkedArtefacts.tsx),
                      // so guard a {value,label}-shaped title instead of rendering an object as a child.
                      const linkTitle = l.artefact_title && typeof l.artefact_title === 'object' ? (l.artefact_title as any).value : l.artefact_title;
                      const label = l.artefact_display_id && l.artefact_display_id !== linkTitle
                        ? `${l.artefact_display_id} ${linkTitle || ''}`.trim()
                        : (linkTitle || l.artefact_display_id || l.artefact_id);
                      return (
                        <span key={l.artefact_id} title={label} className={`np-chip np-tone-${l.artefact_type === 'framework' ? 'primary' : 'info'}${l.artefact_type !== 'framework' ? ' np-tabular' : ''}`} style={{ maxWidth: '100%' }}>
                          {l.artefact_type.charAt(0).toUpperCase() + l.artefact_type.slice(1)} &middot; <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
                        </span>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {addTeamOpen && (
        <AddTeamModal
          existingNames={teamNames}
          onClose={() => setAddTeamOpen(false)}
          onAdd={(name, departmentId) => {
            setPendingTeams((prev) => (prev.some((t) => t.name === name) ? prev : [...prev, { name, departmentId }]));
            openTeam(name);
            setAddTeamOpen(false);
          }}
        />
      )}
    </div>
  );
};
